import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import { 
  TrendingUp, 
  Droplets, 
  Zap, 
  Recycle, 
  Factory, 
  BarChart3,
  Info,
  AlertTriangle,
  Calendar as CalendarIcon
} from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { IndividualChartControls, ChartFilterOptions, ChartExportOptions } from './IndividualChartControls';
import { ChartExportService, ChartData } from '@/utils/chartExportService';

interface UnitGroupedData {
  unit: string;
  category: string;
  data: Array<{
    name: string;
    value: number;
    date: string;
    source: string;
    scope?: string;
    calculation_type?: string;
  }>;
  totalValue: number;
  averageValue: number;
  trend: 'increasing' | 'decreasing' | 'stable';
}

interface ChartConfig {
  type: 'bar' | 'line' | 'pie' | 'area';
  title: string;
  description: string;
  unit: string;
  colorScheme: string[];
}

export const UnitConsistentCharts = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [unitGroupedData, setUnitGroupedData] = useState<UnitGroupedData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('1year');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedChart, setSelectedChart] = useState<UnitGroupedData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Custom date range state
  const [customDateRange, setCustomDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined
  });
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  
  // Individual chart filters state
  const [chartFilters, setChartFilters] = useState<{ [key: string]: ChartFilterOptions }>({});
  
  // Popover state for debugging
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useEffect(() => {
    if (user) {
      console.log('useEffect triggered:', { selectedTimeRange, selectedCategory, isCustomDateRange, customDateRange });
      fetchAndProcessData();
    }
  }, [user, selectedTimeRange, selectedCategory, isCustomDateRange]);

  // Separate useEffect for custom date range changes
  useEffect(() => {
    if (user && isCustomDateRange && customDateRange.from && customDateRange.to) {
      console.log('Custom date range changed, refreshing data:', customDateRange);
      fetchAndProcessData();
    }
  }, [user, customDateRange.from, customDateRange.to]);

  const fetchAndProcessData = async () => {
    try {
      setLoading(true);
      
      // Calculate date range
      let endDate = new Date();
      let startDate = new Date();
      
             if (isCustomDateRange && customDateRange.from && customDateRange.to) {
         // Use custom date range
         startDate = new Date(customDateRange.from);
         endDate = new Date(customDateRange.to);
         
         // Ensure end date is at the end of the day for inclusive range
         endDate.setHours(23, 59, 59, 999);
         
         console.log('Using custom date range:', { startDate, endDate });
       } else {
        // Use predefined time range
        switch (selectedTimeRange) {
          case '3months':
            startDate.setMonth(endDate.getMonth() - 3);
            break;
          case '6months':
            startDate.setMonth(endDate.getMonth() - 6);
            break;
          case '1year':
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
          case '2years':
            startDate.setFullYear(endDate.getFullYear() - 2);
            break;
          default:
            startDate.setFullYear(endDate.getFullYear() - 1);
                 }
       }
       
       console.log('Date range calculated:', { startDate, endDate, isCustomDateRange });
       
       // Fetch data from multiple sources
      const [carbonData, esgData, materialsData] = await Promise.all([
        supabase
          .from('carbon_calculations')
          .select('*')
          .eq('user_id', user?.id)
          .gte('calculation_date', startDate.toISOString().split('T')[0])
          .lte('calculation_date', endDate.toISOString().split('T')[0])
          .order('created_at', { ascending: false }),
        supabase
          .from('user_metrics_data')
          .select(`
            *,
            metric:unified_metrics_library(*)
          `)
          .eq('organization_id', user?.id)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .order('created_at', { ascending: false }),
        supabase
          .from('user_materials_data')
          .select(`
            *,
            material:materials_library(*)
          `)
          .eq('organization_id', user?.id)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .order('created_at', { ascending: false })
      ]);

      // Process and group data by compatible units
      const processedData = processDataByUnits(
        carbonData.data || [],
        esgData.data || [],
        materialsData.data || []
      );

      setUnitGroupedData(processedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch data for charts',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const processDataByUnits = (
    carbonData: any[],
    esgData: any[],
    materialsData: any[]
  ): UnitGroupedData[] => {
    const unitGroups: { [key: string]: UnitGroupedData } = {};

    // Process carbon data
    carbonData.forEach(item => {
      const unit = item.unit || 'tCO2e';
      const category = item.calculation_type || 'carbon';
      
      if (!unitGroups[unit]) {
        unitGroups[unit] = {
          unit,
          category,
          data: [],
          totalValue: 0,
          averageValue: 0,
          trend: 'stable'
        };
      }

      unitGroups[unit].data.push({
        name: item.activity_name || 'Unknown Activity',
        value: Number(item.amount) || 0,
        date: item.calculation_date || item.created_at,
        source: 'Carbon Calculations',
        scope: item.scope,
        calculation_type: item.calculation_type
      });
    });

    // Process ESG data
    esgData.forEach(item => {
      const unit = item.unit || 'unit';
      const category = item.category || 'esg';
      
      if (!unitGroups[unit]) {
        unitGroups[unit] = {
          unit,
          category,
          data: [],
          totalValue: 0,
          averageValue: 0,
          trend: 'stable'
        };
      }

      unitGroups[unit].data.push({
        name: item.metric_name || item.name || 'Unknown Metric',
        value: Number(item.custom_value) || 0,
        date: item.created_at,
        source: 'ESG Metrics',
        scope: item.metric?.scope,
        calculation_type: 'esg'
      });
    });

    // Process materials data
    materialsData.forEach(item => {
      const unit = item.unit || 'kg';
      const category = item.material?.category || 'materials';
      
      if (!unitGroups[unit]) {
        unitGroups[unit] = {
          unit,
          category,
          data: [],
          totalValue: 0,
          averageValue: 0,
          trend: 'stable'
        };
      }

      unitGroups[unit].data.push({
        name: item.material?.name || item.name || 'Unknown Material',
        value: Number(item.custom_value) || 0,
        date: item.created_at,
        source: 'Materials Data',
        scope: item.material?.scope,
        calculation_type: 'material'
      });
    });

    // Calculate totals, averages, and trends for each unit group
    Object.values(unitGroups).forEach(group => {
      group.totalValue = group.data.reduce((sum, item) => sum + item.value, 0);
      group.averageValue = group.totalValue / group.data.length;
      
      // Calculate trend (simple comparison of first and last values)
      if (group.data.length >= 2) {
        const sortedData = [...group.data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const firstValue = sortedData[0].value;
        const lastValue = sortedData[sortedData.length - 1].value;
        const change = ((lastValue - firstValue) / firstValue) * 100;
        
        if (change > 5) group.trend = 'increasing';
        else if (change < -5) group.trend = 'decreasing';
        else group.trend = 'stable';
      }
    });

    // Filter by category if selected
    if (selectedCategory !== 'all') {
      return Object.values(unitGroups).filter(group => 
        group.category.toLowerCase().includes(selectedCategory.toLowerCase())
      );
    }

    return Object.values(unitGroups);
  };

  const getChartConfig = (unit: string): ChartConfig => {
    const configs: { [key: string]: ChartConfig } = {
      'tCO2e': {
        type: 'line',
        title: 'Carbon Emissions Trend',
        description: 'Track your carbon footprint over time',
        unit: 'tCO2e',
        colorScheme: ['#dc2626', '#ef4444', '#f87171']
      },
      'kWh': {
        type: 'bar',
        title: 'Energy Consumption',
        description: 'Monitor energy usage across different activities',
        unit: 'kWh',
        colorScheme: ['#f59e0b', '#fbbf24', '#fcd34d']
      },
      'm³': {
        type: 'area',
        title: 'Water Usage Analysis',
        description: 'Track water consumption and efficiency',
        unit: 'm³',
        colorScheme: ['#0ea5e9', '#38bdf8', '#7dd3fc']
      },
      'kg': {
        type: 'bar',
        title: 'Material Usage',
        description: 'Monitor material consumption and waste',
        unit: 'kg',
        colorScheme: ['#10b981', '#34d399', '#6ee7b7']
      },
      'unit': {
        type: 'pie',
        title: 'ESG Metrics Distribution',
        description: 'Overview of ESG performance indicators',
        unit: 'unit',
        colorScheme: ['#8b5cf6', '#a78bfa', '#c4b5fd']
      }
    };

    return configs[unit] || {
      type: 'bar',
      title: 'Data Analysis',
      description: 'General data visualization',
      unit,
      colorScheme: ['#6b7280', '#9ca3af', '#d1d5db']
    };
  };

  const renderChart = (data: UnitGroupedData, config: ChartConfig, selectedChartType?: string) => {
    const chartData = data.data.map(item => ({
      name: item.name,
      value: item.value,
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }));

    // Use selected chart type if available, otherwise fall back to config.type
    const chartType = selectedChartType && selectedChartType !== 'all' ? selectedChartType : config.type;

    switch (chartType) {
      case 'line':
        return (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={config.colorScheme[0]} 
              strokeWidth={2}
              dot={{ fill: config.colorScheme[0] }}
            />
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" fill={config.colorScheme[0]} />
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="value" 
              stroke={config.colorScheme[0]} 
              fill={config.colorScheme[0]}
              fillOpacity={0.3}
            />
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={config.colorScheme[index % config.colorScheme.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        );

      default:
        return <div>Chart type not supported</div>;
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp className="h-4 w-4 text-red-500" />;
      case 'decreasing':
        return <TrendingUp className="h-4 w-4 text-green-500 transform rotate-180" />;
      default:
        return <BarChart3 className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return 'text-red-600 bg-red-50';
      case 'decreasing':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getChartIcon = (category: string) => {
    switch (category) {
      case 'carbon':
        return <Droplets className="h-5 w-5 text-red-500" />;
      case 'esg':
        return <Zap className="h-5 w-5 text-blue-500" />;
      case 'materials':
        return <Recycle className="h-5 w-5 text-green-500" />;
      default:
        return <Info className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatDateRange = (from: Date, to: Date): string => {
    const fromStr = from.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
    const toStr = to.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
    return `${fromStr} - ${toStr}`;
  };

  const validateCustomDateRange = (from: Date | undefined, to: Date | undefined): boolean => {
    if (!from || !to) return false;
    
    const now = new Date();
    const maxRange = 365 * 5; // 5 years max range
    
    // Check if dates are in the future (allow today)
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (from > today || to > today) return false;
    
    // Check if range is too large
    const diffTime = Math.abs(to.getTime() - from.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > maxRange) return false;
    
    // Check if from date is before to date
    if (from >= to) return false;
    
    return true;
  };

  // Handle individual chart filter changes
  const handleChartFilterChange = (chartId: string, filters: ChartFilterOptions) => {
    setChartFilters(prev => ({
      ...prev,
      [chartId]: filters
    }));
    
    // Apply filters to the specific chart data
    // This could trigger a re-fetch or re-process of data for that specific chart
    console.log(`Chart ${chartId} filters updated:`, filters);
  };

  // Handle individual chart export
  const handleChartExport = async (chartId: string, options: ChartExportOptions) => {
    try {
      const chartElement = ChartExportService.getChartElement(chartId);
      if (!chartElement) {
        throw new Error('Chart element not found');
      }

      const chartData = unitGroupedData.find(chart => 
        `${chart.unit}-${chart.category}` === chartId
      );

      if (!chartData) {
        throw new Error('Chart data not found');
      }

      const exportData = ChartExportService.prepareChartData(
        getChartConfig(chartData.unit).title,
        chartData.data,
        getChartConfig(chartData.unit).type,
        chartFilters[chartId],
        {
          unit: chartData.unit,
          category: chartData.category,
          totalValue: chartData.totalValue,
          averageValue: chartData.averageValue,
          trend: chartData.trend
        }
      );

      await ChartExportService.exportChart(chartElement, options, exportData);
      
      toast({
        title: 'Export Successful',
        description: `Chart exported as ${options.format.toUpperCase()}`,
      });
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Failed to export chart',
        variant: 'destructive'
      });
    }
  };

  // Handle individual chart refresh
  const handleChartRefresh = (chartId: string) => {
    console.log(`Refreshing chart ${chartId}`);
    // This could trigger a re-fetch of data for the specific chart
    fetchAndProcessData();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Unit-Consistent Charts</CardTitle>
          <CardDescription>Loading chart data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Unit-Consistent Analysis</h2>
          <p className="text-muted-foreground">
            Charts grouped by compatible units for accurate comparison and analysis
            {isCustomDateRange && customDateRange.from && customDateRange.to && (
              <span className="block mt-1 text-sm font-medium text-blue-600">
                📅 Custom Period: {formatDateRange(customDateRange.from, customDateRange.to)}
              </span>
            )}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Time Range Selection */}
          <div className="flex items-center gap-2">
            <Select 
              value={selectedTimeRange} 
              onValueChange={(value) => {
                setSelectedTimeRange(value);
                setIsCustomDateRange(false);
                setCustomDateRange({ from: undefined, to: undefined });
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3months">Last 3 Months</SelectItem>
                <SelectItem value="6months">Last 6 Months</SelectItem>
                <SelectItem value="1year">Last Year</SelectItem>
                <SelectItem value="2years">Last 2 Years</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            
                         {/* Custom Date Range Calendar */}
             {selectedTimeRange === 'custom' && (
               <div className="flex items-center gap-2">
                 {isCustomDateRange && customDateRange.from && customDateRange.to && (
                   <Badge variant="secondary" className="text-xs">
                     📅 Active: {formatDateRange(customDateRange.from, customDateRange.to)}
                   </Badge>
                 )}
                 
                 {/* Debug Info */}
                 {process.env.NODE_ENV === 'development' && (
                   <div className="flex items-center gap-2">
                     <Badge variant="outline" className="text-xs">
                       Debug: {isCustomDateRange ? 'Custom' : 'Predefined'} | 
                       From: {customDateRange.from?.toISOString().split('T')[0] || 'None'} | 
                       To: {customDateRange.to?.toISOString().split('T')[0] || 'None'}
                     </Badge>
                     
                     {/* Test Button */}
                     <Button
                       variant="outline"
                       size="sm"
                       onClick={() => {
                         const testFrom = new Date();
                         testFrom.setDate(testFrom.getDate() - 30);
                         const testTo = new Date();
                         testTo.setDate(testTo.getDate() - 1);
                         
                         console.log('Setting test date range:', { testFrom, testTo });
                         setCustomDateRange({ from: testFrom, to: testTo });
                         setIsCustomDateRange(true);
                       }}
                       className="h-6 px-2 text-xs"
                     >
                       Test Range
                     </Button>
                     
                     {/* Popover Test Button */}
                     <Button
                       variant="outline"
                       size="sm"
                       onClick={() => {
                         console.log('Testing Popover, current state:', isCalendarOpen);
                         setIsCalendarOpen(!isCalendarOpen);
                       }}
                       className="h-6 px-2 text-xs"
                     >
                       Test Popover ({isCalendarOpen ? 'Open' : 'Closed'})
                     </Button>
                   </div>
                 )}
                 
                 {/* Calendar Popover */}
                 <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                   <PopoverTrigger asChild>
                     <Button
                       variant="outline"
                       className="w-[140px] justify-start text-left font-normal"
                       onClick={() => {
                         console.log('Calendar button clicked, setting Popover to open');
                         setIsCalendarOpen(true);
                       }}
                     >
                       <CalendarIcon className="mr-2 h-4 w-4" />
                       {customDateRange.from ? (
                         customDateRange.to ? (
                           formatDateRange(customDateRange.from, customDateRange.to)
                         ) : (
                           `${customDateRange.from.toLocaleDateString()} - Select end date`
                         )
                       ) : (
                         "Pick start and end dates"
                       )}
                     </Button>
                   </PopoverTrigger>
                   <PopoverContent 
                     className="w-auto p-4 border shadow-lg bg-white z-50" 
                     align="start" 
                     side="bottom"
                     style={{ 
                       position: 'absolute', 
                       zIndex: 9999, 
                       backgroundColor: 'white',
                       border: '1px solid #ccc',
                       borderRadius: '6px',
                       boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                     }}
                   >
                     <div className="space-y-2">
                       <h4 className="font-medium text-sm">Select Date Range</h4>
                       <Calendar
                         initialFocus
                         mode="range"
                         defaultMonth={customDateRange.from}
                         selected={{
                           from: customDateRange.from,
                           to: customDateRange.to,
                         }}
                         onSelect={(range) => {
                           console.log('Calendar selection:', range);
                           if (range?.from && range?.to) {
                             if (validateCustomDateRange(range.from, range.to)) {
                               console.log('Valid date range selected:', range);
                               setCustomDateRange({
                                 from: range.from,
                                 to: range.to,
                               });
                               setIsCustomDateRange(true);
                               setIsCalendarOpen(false); // Close popover after selection
                             } else {
                               console.log('Invalid date range:', range);
                               toast({
                                 title: 'Invalid Date Range',
                                 description: 'Please select a valid date range (max 5 years, no future dates)',
                                 variant: 'destructive'
                               });
                             }
                           }
                         }}
                         numberOfMonths={2}
                         disabled={(date) => date > new Date()}
                       />
                     </div>
                   </PopoverContent>
                 </Popover>
                 
                 {/* Fallback Date Inputs (in case Popover doesn't work) */}
                 <div className="flex items-center gap-2">
                   <input
                     type="date"
                     className="px-2 py-1 text-xs border rounded"
                     onChange={(e) => {
                       const date = new Date(e.target.value);
                       setCustomDateRange(prev => ({ ...prev, from: date }));
                     }}
                     max={new Date().toISOString().split('T')[0]}
                   />
                   <span className="text-xs text-gray-500">to</span>
                   <input
                     type="date"
                     className="px-2 py-1 text-xs border rounded"
                     onChange={(e) => {
                       const date = new Date(e.target.value);
                       setCustomDateRange(prev => ({ ...prev, to: date }));
                     }}
                     max={new Date().toISOString().split('T')[0]}
                   />
                 </div>
                 
                 {/* Simple Test Popover */}
                 <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                   <PopoverTrigger asChild>
                     <Button variant="outline" size="sm" className="h-6 px-2 text-xs">
                       Test Simple Popover
                     </Button>
                   </PopoverTrigger>
                   <PopoverContent className="w-48 p-2 border shadow-lg bg-white z-50">
                     <div className="text-xs">
                       <p>This is a test popover!</p>
                       <p>If you can see this, Popover is working.</p>
                       <Button 
                         size="sm" 
                         onClick={() => setIsCalendarOpen(false)}
                         className="mt-2 w-full"
                       >
                         Close
                       </Button>
                     </div>
                   </PopoverContent>
                 </Popover>
                
                                 {/* Apply Custom Range Button */}
                 {customDateRange.from && customDateRange.to && !isCustomDateRange && (
                   <Button
                     variant="default"
                     size="sm"
                     onClick={() => {
                       setIsCustomDateRange(true);
                       console.log('Applying custom date range:', customDateRange);
                     }}
                     className="h-8 px-2"
                   >
                     Apply Range
                   </Button>
                 )}
                 
                 {/* Clear Custom Range Button */}
                 {(customDateRange.from || customDateRange.to) && (
                   <Button
                     variant="ghost"
                     size="sm"
                     onClick={() => {
                       setCustomDateRange({ from: undefined, to: undefined });
                       setIsCustomDateRange(false);
                       setSelectedTimeRange('1year');
                     }}
                     className="h-8 px-2"
                   >
                     Clear
                   </Button>
                 )}
              </div>
            )}
          </div>
          
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="carbon">Carbon</SelectItem>
              <SelectItem value="esg">ESG</SelectItem>
              <SelectItem value="materials">Materials</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Charts Grid */}
      {!loading && unitGroupedData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {unitGroupedData.map((chartData, index) => {
            const chartId = `${chartData.unit}-${chartData.category}`;
            const chartConfig = getChartConfig(chartData.unit);
            const currentFilters = chartFilters[chartId] || {
              timeRange: selectedTimeRange,
              category: selectedCategory,
              scope: 'all',
              dataSource: 'all',
              chartType: 'all',
              tags: [],
              customDateRange: isCustomDateRange ? customDateRange : undefined
            };

            return (
              <Card key={chartId} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        {getChartIcon(chartData.category)}
                        {chartConfig.title}
                      </CardTitle>
                      <CardDescription>
                        {chartConfig.description} - {chartData.data.length} data points
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedChart(chartData);
                        setIsModalOpen(true);
                      }}
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>

                {/* Individual Chart Controls */}
                <div className="px-6 pb-3">
                  <IndividualChartControls
                    chartId={chartId}
                    chartTitle={chartConfig.title}
                    chartType={chartConfig.type}
                    filters={currentFilters}
                    onFiltersChange={(filters) => handleChartFilterChange(chartId, filters)}
                    onExport={(options) => handleChartExport(chartId, options)}
                    onRefresh={() => handleChartRefresh(chartId)}
                    onViewDetails={() => {
                      setSelectedChart(chartData);
                      setIsModalOpen(true);
                    }}
                                         availableFilters={{
                       timeRanges: ['3months', '6months', '1year', '2years', 'custom'],
                       categories: ['all', 'carbon', 'esg', 'materials'],
                       scopes: ['all', 'scope1', 'scope2', 'scope3'],
                       dataSources: ['all', 'carbon_calculations', 'user_metrics_data', 'user_materials_data'],
                       chartTypes: ['all', 'bar', 'line', 'pie', 'area'],
                       tags: [], // This could be populated with available tags for the chart
                       customDateRange: isCustomDateRange ? customDateRange : undefined
                     }}
                    className="mb-3"
                    showAdvancedFilters={false}
                  />
                </div>

                <CardContent className="pt-0">
                  <div 
                    id={chartId}
                    data-chart-id={chartId}
                    className="w-full h-[300px]"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      {renderChart(chartData, chartConfig, currentFilters.chartType)}
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-4">
                      <span>Total: {chartData.totalValue.toFixed(2)} {chartData.unit}</span>
                      <span>Avg: {chartData.averageValue.toFixed(2)} {chartData.unit}</span>
                    </div>
                    <Badge className={getTrendColor(chartData.trend)}>
                      {getTrendIcon(chartData.trend)}
                      <span className="ml-1 capitalize">{chartData.trend}</span>
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* No Data State */}
      {!loading && unitGroupedData.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Data Available</h3>
            <p className="text-muted-foreground text-center mb-4">
              No data found for the selected time range and category. Try adjusting your filters or add some data.
            </p>
            <Button onClick={() => navigate('/data-management')}>
              Add Data
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {selectedChart ? `${getChartConfig(selectedChart.unit).title} - Details` : 'Chart Details'}
            </DialogTitle>
            <DialogDescription>
              Detailed breakdown of data points and analysis
            </DialogDescription>
          </DialogHeader>
          
          {selectedChart && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">Data Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Unit:</span>
                      <Badge variant="outline">{selectedChart.unit}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Category:</span>
                      <Badge variant="outline">{selectedChart.category}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Value:</span>
                      <span className="font-medium">{selectedChart.totalValue.toFixed(2)} {selectedChart.unit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Average Value:</span>
                      <span className="font-medium">{selectedChart.averageValue.toFixed(2)} {selectedChart.unit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Data Points:</span>
                      <span className="font-medium">{selectedChart.data.length}</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Trend Analysis</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span>Current Trend:</span>
                      <Badge className={getTrendColor(selectedChart.trend)}>
                        {getTrendIcon(selectedChart.trend)}
                        <span className="ml-1 capitalize">{selectedChart.trend}</span>
                      </Badge>
                    </div>
                    <div className="text-gray-600">
                      {selectedChart.trend === 'increasing' && 
                        'Values are trending upward. Consider implementing efficiency measures.'}
                      {selectedChart.trend === 'decreasing' && 
                        'Values are trending downward. Good progress on sustainability goals.'}
                      {selectedChart.trend === 'stable' && 
                        'Values are relatively stable. Monitor for any significant changes.'}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Data Points</h4>
                <div className="max-h-64 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedChart.data.map((item, index) => (
                      <div key={index} className="p-2 border rounded text-sm">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-gray-600">
                          {item.value.toFixed(2)} {selectedChart.unit}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(item.date).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          Source: {item.source}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
